import { TradeModel } from "api/models/company/trade-model";
import { I18N } from "aurelia-i18n";
import { autoinject, computedFrom } from "aurelia-framework";
import dateHelper from "helpers/dateHelper";
import queryStringHelper from "helpers/queryStringHelper";
import requisitionService from "services/requisitionService";
import settingRepository from "repositories/settingRepository";
import equipmentTypeService from "services/equipmentTypeService";
import { TradeService } from "services/trade-service";
import { SkillService } from "services/skill-service";
import _ from "underscore";
import routerHelper from "helpers/routerHelper";

import { NotificationHelper } from "helpers/notification-helper";
import { Router } from "aurelia-router";
import { FormBase } from "pages/form-base";

@autoinject
export class DailyEntryLaborRequisitionEdit extends FormBase {

    public LABOR_TYPE: string = "Trade";
    public EQUIPMENT_TYPE: string = "EquipmentType";

    public dispatchDate: any;
    public dispatchProjectCode: any;
    public id: any;
    public isProjectResponsible: boolean = false;
    public type: string = this.LABOR_TYPE;

    public trade: any;
    public skill: any;
    public equipmentType: any;
    public quantity: number = 1;
    public requiredDate: any = dateHelper.addHour(dateHelper.getNow(), 24);
    public comment: string = "";
    public templateCode: string | null = "";
    public isEdit: boolean = false;
    public unModified: any = null;

    @computedFrom("type", "isEdit")
    public get equipmentTypeClass(): string {
        let cls = this.type === this.EQUIPMENT_TYPE ? "active" : "";

        cls += this.isEdit ? " disabled" : "";

        return cls;
    }

    @computedFrom("type", "isEdit")
    public get laborTypeClass(): string {
        let cls = this.type === this.LABOR_TYPE ? "active" : "";

        cls += this.isEdit ? " disabled" : "";

        return cls;
    }

    constructor(i18n: I18N, notificationHelper: NotificationHelper, router: Router, private readonly skillService: SkillService, private readonly tradesService: TradeService) {
        super(notificationHelper, i18n, router);
    }

    public checkDirty(): boolean {
        if (!this.unModified) {
            return false;
        }

        if (!this.isEdit) {
            this.unModified = this.buildRequisitionBlankDto();
        }
        this.unModified.RequestDate = this.unModified.RequestDate && this.unModified.RequestDate !== "" ? new Date(new Date(this.unModified.RequestDate.toString()).getFullYear(), new Date(new Date(this.unModified.RequestDate.toString())).getMonth(), new Date(new Date(this.unModified.RequestDate.toString())).getDate(), 0, 0, 0, 0) : this.unModified.RequestDate;
        const stringifyUnmodified = JSON.stringify(this.unModified).replace(/[^0-9A-Z]+/gi, "");

        const modified = this.buildRequisitionDto();
        modified.RequestDate = modified.RequestDate && modified.RequestDate !== "" ? new Date(new Date(modified.RequestDate.toString()).getFullYear(), new Date(new Date(modified.RequestDate.toString())).getMonth(), new Date(new Date(modified.RequestDate.toString())).getDate(), 0, 0, 0, 0) : modified.RequestDate;
        const stringifyCurrent = JSON.stringify(modified).replace(/[^0-9A-Z]+/gi, "");

        return stringifyUnmodified !== stringifyCurrent;
    }

    public async activate(params: any): Promise<any> {
        this.bindViewModel(params.dispatchProjectCode, params.dailyEntryDate, params.id, params.q);
        await this.loadData();
    }

    public bindViewModel(dispatchProjectCode: string, dispatchDate: Date, id: number, queryString: string): any {
        this.isProjectResponsible = queryStringHelper.parseIsProjectResponsible(queryString);

        this.id = id;
        this.dispatchDate = dispatchDate;
        this.dispatchProjectCode = dispatchProjectCode;

        this.isEdit = this.id !== null && this.id !== undefined;
    }

    public async loadData(): Promise<any> {
        this.templateCode = settingRepository.getDispatchTemplate();
        this.unModified = this.buildRequisitionBlankDto();
        if (this.isEdit) {
            this.loadRequisition(await requisitionService.getRequisition(this.dispatchProjectCode, this.id));
            this.unModified = this.buildRequisitionDto();
        }
    }

    public loadRequisition(data: any): void {
        this.trade = null;
        this.equipmentType = null;
        this.type = data.ResourceType;

        if (data.ResourceType === this.LABOR_TYPE) {
            this.trade = { id: data.ResourceCode, text: data.ResourceCode + " - " + data.ResourceName };
            if (data.SkillCode) {
                this.skill = { id: data.SkillCode, text: data.SkillCode + " - " + data.SkillDescription };
            } else {
                this.skill = null;
            }
        } else if (data.ResourceType === this.EQUIPMENT_TYPE) {
            this.equipmentType = { id: data.ResourceCode, text: data.ResourceCode + " - " + data.ResourceName };
        }

        this.quantity = data.Quantity;
        this.requiredDate = data.RequestDate;
        this.comment = data.Comment;
    }

    public buildRequisitionDto(): any {
        const dto: any = {};

        if (this.isEdit) {
            dto.Id = this.id;
        }

        dto.ResourceType = this.type;
        if (this.type === this.LABOR_TYPE) {
            if (this.trade) {
                dto.ResourceCode = this.trade.id;
            }
            if (this.skill) {
                dto.SkillCode = this.skill.id;
            }
        } else if (this.type === this.EQUIPMENT_TYPE && this.equipmentType) {
            dto.ResourceCode = this.equipmentType.id;
        }

        dto.Quantity = this.quantity;
        dto.RequestDate = this.requiredDate;
        dto.Comment = this.comment;
        dto.TemplateCode = this.templateCode;

        return dto;
    }

    public buildRequisitionBlankDto(): any {
        const dto: any = {};

        dto.ResourceType = this.type;

        dto.ResourceCode = "";
        if (this.type === this.LABOR_TYPE) {
            dto.SkillCode = "";
        }

        dto.Quantity = 0;
        dto.RequestDate = dateHelper.addHour(dateHelper.getNow(), 24);
        dto.Comment = "";
        dto.TemplateCode = this.templateCode;

        return dto;
    }

    public isValid(): boolean {
        if (!this.isTradeValid() || !this.isEquipmentTypeValid() || !this.isQuantityValid() || !this.isRequiredDateValid()) {
            return false;
        }
        return true;
    }

    public isTradeValid(): boolean {
        if (this.type === this.LABOR_TYPE && !this.trade) {
            this.notificationHelper.showError(this.i18n.tr("err_TradeRequired"));
            return false;
        }

        return true;
    }

    public isEquipmentTypeValid(): boolean {
        if (this.type === this.EQUIPMENT_TYPE && !this.equipmentType) {
            this.notificationHelper.showError(this.i18n.tr("err_EquipmentTypeRequired"));
            return false;
        }

        return true;
    }

    public isQuantityValid(): boolean {
        if (!this.quantity) {
            this.notificationHelper.showError(this.i18n.tr("err_QuantityRequired"));
            return false;
        }
        if (this.quantity < 1) {
            this.notificationHelper.showError(this.i18n.tr("err_MinQuantity"));
            return false;
        }

        return true;
    }

    public isRequiredDateValid(): boolean {
        if (!this.requiredDate) {
            this.notificationHelper.showError(this.i18n.tr("err_RequiredDateRequired"));
            return false;
        }

        return true;
    }

    public get getEquipmentTypes(): any {
        return {
            transport: (params: any, success: any): any => {
                equipmentTypeService.getEquipmentTypeForCompany(params.data.filter, params.data.page || 1)
                .then((data: any) => {
                    return success(data);
                });
            }
        };
    }

    public get getSkills(): any {
        return {
            transport: (params: any, success: any): any => {
                this.skillService.getSkills(params.data.filter, params.data.page || 1)
                .then((data: any) => {
                    return success(data);
                });
            }
        };
    }

    public get getTrades(): any {
        return {
            transport: (params: any, success: any): any => {
                this.tradesService.GetTrades(params.data.filter, params.data.page || 1)
                .then((data: TradeModel[]) => {
                    return success(data);
                });
            },
            mapResults: (item: TradeModel): any => {
                return { id: item.Code, text: item.Code + " - " + item.Description, data: item };
            }
        };
    }

    public show(tab: string): void {
        this.type = tab;
    }

    public async save(): Promise<any> {
        if (this.isValid()) {
            this.unModified = null;
            await requisitionService.saveRequisition(this.dispatchProjectCode, this.dispatchDate, this.buildRequisitionDto());
            routerHelper.navigateBack();
        }
    }

}
